import React, { useState, useEffect, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { LoadingSpinner, ConfirmDialog, ManagementModuleTitle } from "../../../components/Shared";
import { RiskAssessmentsTable } from "../../../components/Admin/RiskAssessments";
import { RiskAssessmentForm } from "../../../components/Admin/RiskAssessments/RiskAssessmentForm/RiskAssessmentForm";
import { useLanguage, useSite } from "../../../contexts";
import { useCompany } from "../../../contexts/CompanyContext";
import { useAuth } from "../../../hooks";
import { RiskAssessment, Company, Site, Sector, RiskType } from "../../../api";
import "./RiskAssessments.scss";

const riskCtrl       = new RiskAssessment();
const companyCtrl    = new Company();
const siteCtrl       = new Site();
const sectorCtrl     = new Sector();
const riskTypeCtrl   = new RiskType();

// Lookup matrix 5×5 (Sev × FoE → nivel), product ranges: ≥17 CRITICO, ≥10 ALTO, ≥5 MODERADO, else BAJO
const MATRIX_5x5 = {
  5: { 1: "MODERADO", 2: "ALTO",     3: "ALTO",     4: "CRITICO",  5: "CRITICO"  },
  4: { 1: "BAJO",     2: "MODERADO", 3: "ALTO",     4: "ALTO",     5: "CRITICO"  },
  3: { 1: "BAJO",     2: "MODERADO", 3: "MODERADO", 4: "ALTO",     5: "ALTO"     },
  2: { 1: "BAJO",     2: "BAJO",     3: "MODERADO", 4: "MODERADO", 5: "ALTO"     },
  1: { 1: "BAJO",     2: "BAJO",     3: "BAJO",     4: "BAJO",     5: "MODERADO" },
};

const INSST_MATRIX = {
  A: { ED: "CRITICO",  D: "ALTO",     LD: "MODERADO" },
  M: { ED: "ALTO",     D: "MODERADO", LD: "BAJO"     },
  B: { ED: "MODERADO", D: "BAJO",     LD: "BAJO"     },
};


export function getLevel(r) {
  if (r.riskResult) {
    if (["CRITICO","ALTO","MODERADO","BAJO"].includes(r.riskResult)) return r.riskResult;
    // backward compat con registros guardados con los strings viejos
    const MAP = {
      critical_immediate_action: "CRITICO",
      high_short_term_action:    "ALTO",
      moderate_monitor:          "MODERADO",
      low_acceptable:            "BAJO",
    };
    if (MAP[r.riskResult]) return MAP[r.riskResult];
  }
  const m = r.methodology || "5x5";
  if (m === "NTP330" && r.nd && r.ne && r.nc) {
    const nr = r.nd * r.ne * r.nc;
    if (nr >= 600) return "CRITICO";
    if (nr >= 150) return "ALTO";
    if (nr >= 40)  return "MODERADO";
    return "BAJO";
  }
  if (m === "INSST" && r.probability && r.damageConsequence) {
    return INSST_MATRIX[r.probability]?.[r.damageConsequence] || "BAJO";
  }
  const sev = Number(r.severity);
  const foe = Number(r.exposureFrequency);
  if (!sev || !foe) return null;
  return MATRIX_5x5[sev]?.[foe] || "BAJO";
}

const LEVEL_CONFIG = {
  CRITICO:  { color: "#B23030", bg: "#F8D5D5", label: "Crítico"  },
  ALTO:     { color: "#E07020", bg: "#FBE1CC", label: "Alto"     },
  MODERADO: { color: "#D4960F", bg: "#FEF3D6", label: "Moderado" },
  BAJO:     { color: "#2E9461", bg: "#D7EFE0", label: "Bajo"     },
};


const FilterDropdown = ({ options, value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="ra-fd" ref={ref}>
      <button type="button" className="ra-fd-toggle" onClick={() => setOpen((v) => !v)}>
        <span className={selected ? "" : "ra-fd-placeholder"}>{selected ? selected.text : placeholder}</span>
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`ra-fd-arrow${open ? " ra-fd-arrow--open" : ""}`}>
          <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
        </svg>
      </button>
      {open && (
        <div className="ra-fd-menu">
          <button
            type="button"
            className={`ra-fd-item${!value ? " ra-fd-item--active" : ""}`}
            onClick={() => { onChange(""); setOpen(false); }}
          >
            {placeholder}
          </button>
          {options.map((o) => (
            <button
              type="button"
              key={o.key}
              className={`ra-fd-item${o.value === value ? " ra-fd-item--active" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Variante de selección múltiple: el menú queda abierto al marcar opciones.
const MultiFilterDropdown = ({ options, values, onChange, placeholder, clearLabel }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = options.filter((o) => values.includes(o.value));
  const label = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? selected[0].text
      : `${placeholder} (${selected.length})`;

  const toggle = (v) => onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);

  return (
    <div className="ra-fd" ref={ref}>
      <button type="button" className="ra-fd-toggle" onClick={() => setOpen((v) => !v)}>
        <span className={selected.length ? "" : "ra-fd-placeholder"}>{label}</span>
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`ra-fd-arrow${open ? " ra-fd-arrow--open" : ""}`}>
          <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
        </svg>
      </button>
      {open && (
        <div className="ra-fd-menu">
          {options.map((o) => {
            const checked = values.includes(o.value);
            return (
              <button
                type="button"
                key={o.key}
                className={`ra-fd-item ra-fd-item--multi${checked ? " ra-fd-item--active" : ""}`}
                onClick={() => toggle(o.value)}
              >
                <span className={`ra-fd-check${checked ? " ra-fd-check--on" : ""}`} />
                {o.color && <span className="ra-lvl-dot" style={{ background: o.color }} />}
                {o.text}
              </button>
            );
          })}
          {values.length > 0 && (
            <button type="button" className="ra-fd-item ra-fd-clear" onClick={() => onChange([])}>
              {clearLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export function RiskAssessments() {
  const { accessToken, user: { role } }  = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const levelConfigT = {
    CRITICO:  { ...LEVEL_CONFIG.CRITICO,  label: t("ra_level_critical")  },
    ALTO:     { ...LEVEL_CONFIG.ALTO,     label: t("ra_level_high")      },
    MODERADO: { ...LEVEL_CONFIG.MODERADO, label: t("ra_level_moderate")  },
    BAJO:     { ...LEVEL_CONFIG.BAJO,     label: t("ra_level_low")       },
  };
  const { site }    = useSite();
  const { company } = useCompany();

  const [items,        setItems]        = useState(null);
  const [reload,       setReload]       = useState(false);
  const [showForm,     setShowForm]     = useState(false);
  const [selected,     setSelected]     = useState(null);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [confirmItem,  setConfirmItem]  = useState(null);
  const [companyName,  setCompanyName]  = useState("");
  const [siteName,     setSiteName]     = useState("");
  const [exportOpen,   setExportOpen]   = useState(false);
  const [transferSlot, setTransferSlot] = useState(null);

  const currentYear = new Date().getFullYear();
  const [filterYear,     setFilterYear]     = useState(currentYear);
  const [filterSearch,   setFilterSearch]   = useState("");
  const [filterLevels,   setFilterLevels]   = useState([]);
  const [filterMeth,     setFilterMeth]     = useState("5x5");
  const [filterRiskType, setFilterRiskType] = useState("");
  const [filterArea,     setFilterArea]     = useState("");
  const [filterStatus,   setFilterStatus]   = useState("");
  const [filterSite,     setFilterSite]     = useState("");
  const [sites,          setSites]          = useState([]);
  const [companySiteIds, setCompanySiteIds] = useState([]);
  const [allSectors,     setAllSectors]     = useState([]);
  const [allRiskTypes,   setAllRiskTypes]   = useState([]);

  const [showYearMenu, setShowYearMenu] = useState(false);
  const yearMenuRef = useRef(null);

  useEffect(() => {
    if (!showYearMenu) return;
    const handler = (e) => {
      if (yearMenuRef.current && !yearMenuRef.current.contains(e.target))
        setShowYearMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showYearMenu]);

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).reverse();

  const VIEW_OPTIONS = [
    { value: "5x5",    label: "Matriz 5×5"   },
    { value: "NTP330", label: "NTP 330"       },
    { value: "INSST",  label: "Método INSST"  },
  ];
  const currentViewLabel = VIEW_OPTIONS.find(o => o.value === filterMeth)?.label || "Matriz 5×5";

  const hasCompany  = company && company !== "0" && company !== 0;
  const noSiteInCtx = !site || site === "0" || site === 0;

  const noCompanyInCtx = !company || company === "0" || company === 0;

  useEffect(() => {
    if (!accessToken) return;
    const load = !noCompanyInCtx
      ? siteCtrl.getSitesByCompany(accessToken, company, true)
      : siteCtrl.getSites(accessToken, true);
    load.then(res => setSites(Array.isArray(res) ? res : [])).catch(() => {});
    sectorCtrl.getSectors(accessToken, true).then(res => setAllSectors(Array.isArray(res) ? res : [])).catch(() => {});
    riskTypeCtrl.getRiskTypes(accessToken, true).then(res => setAllRiskTypes(Array.isArray(res) ? res : [])).catch(() => {});
  }, [accessToken, company, noCompanyInCtx]);

  useEffect(() => {
    if (noCompanyInCtx) { setCompanySiteIds([]); return; }
    siteCtrl.getSitesByCompany(accessToken, company, true)
      .then(res => setCompanySiteIds((res || []).map(s => s._id)))
      .catch(() => setCompanySiteIds([]));
  }, [accessToken, company, noCompanyInCtx]);

  useEffect(() => { setFilterSite(""); }, [site, company]);

  useEffect(() => {
    if (!hasCompany) { setItems([]); return; }
    (async () => {
      try {
        setItems(null);
        const params = { active: true };
        const activeSite = (!noSiteInCtx ? site : filterSite) || null;
        if (activeSite) params.site = activeSite;
        const res = await riskCtrl.getRiskAssessments(accessToken, params);
        setItems(res);
      } catch { setItems([]); }
    })();
  }, [reload, site, filterSite, accessToken, hasCompany, noSiteInCtx]);

  useEffect(() => {
    if (!company || company === "0") { setCompanyName(""); return; }
    companyCtrl.getCompany(accessToken, company)
      .then(c => setCompanyName(c?.name || ""))
      .catch(() => {});
  }, [company, accessToken]);

  useEffect(() => {
    if (!site || site === "0") { setSiteName(""); return; }
    siteCtrl.getSite(accessToken, site)
      .then(s => setSiteName(s?.name || ""))
      .catch(() => {});
  }, [site, accessToken]);

  const onReload = () => setReload(p => !p);
  const openNew  = () => { setSelected(null); setShowForm(true); };
  const openEdit = (r) => { setSelected(r);   setShowForm(true); };

  const openDelete = (r) => {
    setConfirmItem(r);
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    try {
      await riskCtrl.deleteRiskAssessment(accessToken, confirmItem._id);
      toast.success("Evaluación eliminada");
      setShowConfirm(false);
      onReload();
    } catch (err) { toast.error(err?.msg || "Error"); }
  };

  // Year-filtered base (for matrices)
  const yearFiltered = (items || []).filter(r => {
    if (!filterYear) return true;
    const d = r.evaluationDate || r.createdAt;
    return d ? new Date(d).getFullYear() === filterYear : true;
  });

  // 5×5 heatmap counts
  const heatmapCounts = {};
  yearFiltered.filter(r => !r.methodology || r.methodology === "5x5").forEach(r => {
    const sev = Number(r.severity);
    const foe = Number(r.exposureFrequency);
    if (sev >= 1 && sev <= 5 && foe >= 1 && foe <= 5) {
      const key = `${sev}-${foe}`;
      heatmapCounts[key] = (heatmapCounts[key] || 0) + 1;
    }
  });

  // NTP330 4×4 heatmap counts (ND × NE)
  const ntpCounts = {};
  yearFiltered.filter(r => r.methodology === "NTP330").forEach(r => {
    if (r.nd != null && r.ne != null) {
      const key = `${r.nd}-${r.ne}`;
      ntpCounts[key] = (ntpCounts[key] || 0) + 1;
    }
  });

  // INSST 3×3 matrix counts
  const insstCounts = {};
  yearFiltered.filter(r => r.methodology === "INSST").forEach(r => {
    if (r.probability && r.damageConsequence) {
      const key = `${r.probability}-${r.damageConsequence}`;
      insstCounts[key] = (insstCounts[key] || 0) + 1;
    }
  });

  const PROB_LABELS = { A: "Alta", M: "Media", B: "Baja" };
  const DC_LABELS   = { LD: "L. Dañino", D: "Dañino", ED: "Ext. Dañino" };

  // OHSAS codes for INSST 3×3 cells
  const OHSAS_CODE = {
    A: { ED: "IN", D: "I",  LD: "MO" },
    M: { ED: "I",  D: "MO", LD: "TO" },
    B: { ED: "MO", D: "TO", LD: "T"  },
  };

  // NTP330: NP level from NP = ND × NE
  const getNPLevel = (nd, ne) => {
    if (nd === 0) return "na";
    const np = nd * ne;
    if (np >= 24) return "CRITICO";   // NP-I  Muy Alto
    if (np >= 10) return "ALTO";      // NP-II  Alto
    if (np >= 4)  return "MODERADO";  // NP-III Medio
    return "BAJO";                    // NP-IV  Bajo
  };

  const ND_VALUES = [10, 6, 2, 0];
  const NE_VALUES = [1, 2, 3, 4];
  const ND_LABELS = { 10: "MA (10)", 6: "A (6)", 2: "M (2)", 0: "B (0)" };

  // Filtered items
  const filtered = (items || []).filter(r => {
    // Company/site filtering
    if (!noSiteInCtx) {
      if ((r.site?._id || r.site) !== site) return false;
    } else if (!filterSite && !noCompanyInCtx && companySiteIds.length > 0) {
      if (!companySiteIds.includes(r.site?._id || r.site)) return false;
    } else if (filterSite) {
      if ((r.site?._id || r.site) !== filterSite) return false;
    }

    if (filterYear) {
      if (r.year !== undefined && r.year !== null) {
        if (r.year !== filterYear) return false;
      } else {
        const dateStr = r.evaluationDate || r.createdAt;
        if (dateStr && new Date(dateStr).getFullYear() !== filterYear) return false;
      }
    }
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      if (
        !r.area?.toLowerCase().includes(q) &&
        !r.jobPosition?.toLowerCase().includes(q) &&
        !r.hazardSource?.toLowerCase().includes(q) &&
        !r.riskType?.toLowerCase().includes(q) &&
        !r.potentialDamage?.toLowerCase().includes(q) &&
        !r.specificControlMeasure?.toLowerCase().includes(q)
      ) return false;
    }
    if (filterLevels.length > 0 && !filterLevels.includes(getLevel(r))) return false;
    if (filterMeth && (r.methodology || "5x5") !== filterMeth) return false;
    if (filterRiskType && r.riskType !== filterRiskType) return false;
    if (filterArea && (r.sector?._id || r.sector) !== filterArea) return false;
    if (filterStatus && r.controlStatus !== filterStatus) return false;
    return true;
  });

  // KPIs — sobre los riesgos filtrados
  const kpiTotal    = filtered.length;
  const kpiCritico  = filtered.filter(r => getLevel(r) === "CRITICO").length;
  const kpiAlto     = filtered.filter(r => getLevel(r) === "ALTO").length;
  const kpiModerado = filtered.filter(r => getLevel(r) === "MODERADO").length;
  const kpiBajo     = filtered.filter(r => getLevel(r) === "BAJO").length;

  // % reducción tras controles (solo 5x5 y NTP330, requiere valor numérico inicial y residual)
  const reducibleItems = filtered.filter(r => {
    const m = r.methodology || "5x5";
    if (m !== "5x5" && m !== "NTP330") return false;
    const initial = r.riskLevel ?? (r.severity && r.exposureFrequency ? r.severity * r.exposureFrequency : null);
    const residual = r.residualRiskValue ?? (
      m === "5x5"
        ? (r.residualSeverity && r.residualFoe ? r.residualSeverity * r.residualFoe : null)
        : (r.residualNd && r.residualNe && r.residualNc ? r.residualNd * r.residualNe * r.residualNc : null)
    );
    return initial > 0 && residual != null && residual >= 0;
  });
  const kpiReduccion = reducibleItems.length > 0
    ? Math.round(
        reducibleItems.reduce((sum, r) => {
          const m = r.methodology || "5x5";
          const initial = r.riskLevel ?? (r.severity * r.exposureFrequency);
          const residual = r.residualRiskValue ?? (
            m === "5x5" ? r.residualSeverity * r.residualFoe : r.residualNd * r.residualNe * r.residualNc
          );
          return sum + ((initial - residual) / initial * 100);
        }, 0) / reducibleItems.length
      )
    : null;

  // Sectores desde catálogo
  const areas = allSectors.map(s => ({ id: s._id, name: s.name })).filter(s => s.name);

  // Tipos de peligro desde catálogo
  const riskTypes = allRiskTypes.map(rt => rt.name || rt).filter(Boolean).sort();

  const toggleLevel = (lvl) => {
    setFilterLevels(prev => prev.includes(lvl) ? prev.filter(l => l !== lvl) : [...prev, lvl]);
  };

  const EXPORT_HEADERS = [
    "N°", "Metodología", "Sector", "Área", "Puesto", "Fuente peligro",
    "Tipo riesgo", "Daño potencial", "Trabajadores expuestos", "Trab. sensibles",
    "Valor inicial", "Nivel inicial", "Valor residual", "Nivel residual",
    "Jerarquía control", "Estado control", "Medida control",
    "Responsable", "Evaluador", "Fecha evaluación", "Próxima reevaluación",
  ];

  const buildExportRows = () =>
    filtered.map((r, i) => [
      r.customId || i + 1,
      r.methodology || "5x5",
      r.sector?.name || "",
      r.area || "",
      r.jobPosition || "",
      r.hazardSource || "",
      r.riskType || "",
      r.potentialDamage || "",
      r.workersExposed ?? "",
      r.sensibleWorkers === "yes" ? "Sí" : r.sensibleWorkers === "no" ? "No" : "",
      r.riskLevel ?? (r.severity && r.exposureFrequency ? r.severity * r.exposureFrequency : ""),
      getLevel(r) || "",
      r.residualRiskValue ?? (r.residualSeverity && r.residualFoe ? r.residualSeverity * r.residualFoe : ""),
      r.residualRisk || "",
      r.controlHierarchy || "",
      r.controlStatus || "",
      r.specificControlMeasure || "",
      r.controlResponsible || "",
      r.evaluator || "",
      r.evaluationDate    ? new Date(r.evaluationDate).toLocaleDateString("es-AR")    : "",
      r.nextReassessment  ? new Date(r.nextReassessment).toLocaleDateString("es-AR")  : "",
    ]);

  const exportMeta = () => {
    const parts = [];
    if (companyName) parts.push(`Empresa: ${companyName}`);
    if (siteName)    parts.push(`Sitio: ${siteName}`);
    parts.push(`Fecha: ${new Date().toLocaleDateString("es-AR")}`);
    parts.push(`Registros: ${filtered.length}`);
    return parts.join("   |   ");
  };

  const exportCsv = () => {
    if (!filtered.length) return;
    const rows = buildExportRows();
    const meta = [
      companyName ? ["Empresa", companyName] : null,
      siteName    ? ["Sitio",   siteName]    : null,
      ["Fecha de exportación", new Date().toLocaleDateString("es-AR")],
      [],
    ].filter(Boolean);
    const all = [...meta, EXPORT_HEADERS, ...rows]
      .map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["﻿" + all], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "evaluacion_riesgos.csv");
  };

  const exportXlsx = () => {
    if (!filtered.length) return;
    const rows = buildExportRows();
    const meta = [
      ...(companyName ? [["Empresa", companyName]] : []),
      ...(siteName    ? [["Sitio",   siteName]]    : []),
      ["Fecha de exportación", new Date().toLocaleDateString("es-AR")],
      [],
    ];
    const ws = XLSX.utils.aoa_to_sheet([...meta, EXPORT_HEADERS, ...rows]);
    ws["!cols"] = EXPORT_HEADERS.map((h, i) => ({
      wch: Math.max(h.length, ...rows.map(r => String(r[i] ?? "").length), 10),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Evaluación de Riesgos");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "evaluacion_riesgos.xlsx");
  };

  const LEVEL_COLORS_PDF = {
    CRITICO:  [178, 48,  48],
    ALTO:     [224, 112, 32],
    MODERADO: [212, 150, 15],
    BAJO:     [46,  148, 97],
  };

  const exportPdf = () => {
    if (!filtered.length) return;
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const consultDate = new Date().toLocaleDateString("es-AR");

    // ── Top rule ─────────────────────────────────────────────────
    doc.setDrawColor(31, 41, 55);
    doc.setLineWidth(0.8);
    doc.line(14, 10, pageWidth - 14, 10);

    // Title + branding
    doc.setFontSize(18);
    doc.setFont(undefined, "bold");
    doc.setTextColor(17, 24, 39);
    doc.text("Evaluación de Riesgos", 14, 19);

    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.setTextColor(156, 163, 175);
    doc.text("Sustemia", pageWidth - 14, 14, { align: "right" });

    // Company + site
    let metaY = 27;
    if (companyName) {
      doc.setFontSize(11);
      doc.setFont(undefined, "bold");
      doc.setTextColor(17, 24, 39);
      doc.text(companyName, 14, metaY);
      metaY += 6;
    }
    if (!noSiteInCtx && siteName) {
      doc.setFontSize(9);
      doc.setFont(undefined, "normal");
      doc.setTextColor(107, 114, 128);
      doc.text(`Establecimiento: ${siteName}`, 14, metaY);
      metaY += 5;
    }

    // Date — right-aligned
    doc.setFontSize(9);
    doc.setFont(undefined, "normal");
    doc.setTextColor(107, 114, 128);
    doc.text(`Fecha de consulta: ${consultDate}`, pageWidth - 14, 27, { align: "right" });

    // Bottom rule
    doc.setDrawColor(209, 213, 219);
    doc.setLineWidth(0.4);
    doc.line(14, metaY + 2, pageWidth - 14, metaY + 2);

    const tableStartY = metaY + 6;

    // Headers cortos para PDF (evitar wrapping en columnas angostas)
    const PDF_HEADERS = [
      "N°", "Metod.", "Sector", "Área", "Puesto",
      "Fuente peligro", "Tipo riesgo", "Daño potencial",
      "Trab. exp.", "T. sensibles",
      "Val. inic.", "Niv. inic.", "Val. resid.", "Niv. resid.",
      "Jerarq. ctrl.", "Estado ctrl.", "Medida ctrl.",
      "Responsable", "Evaluador", "Fecha eval.", "Próx. reevalúa.",
    ];

    const CONTROL_STATUS_LABEL = { NONE: "No implementado", IMPL: "Implementado" };

    // Trunca texto largo solo para el PDF para evitar filas enormes
    const tr = (s, n) => { const str = String(s ?? ""); return str.length > n ? str.slice(0, n) + "…" : str; };

    const pdfRows = filtered.map((r, i) => [
      r.customId || i + 1,
      tr(r.methodology || "5x5", 8),
      tr(r.sector?.name, 18),
      tr(r.area, 18),
      tr(r.jobPosition, 18),
      tr(r.hazardSource, 35),
      tr(r.riskType, 28),
      tr(r.potentialDamage, 35),
      r.workersExposed ?? "",
      r.sensibleWorkers === "yes" ? "Sí" : r.sensibleWorkers === "no" ? "No" : "",
      r.riskLevel ?? (r.severity && r.exposureFrequency ? r.severity * r.exposureFrequency : ""),
      getLevel(r) || "",
      r.residualRiskValue ?? (r.residualSeverity && r.residualFoe ? r.residualSeverity * r.residualFoe : ""),
      tr(r.residualRisk, 12),
      tr(r.controlHierarchy, 22),
      CONTROL_STATUS_LABEL[r.controlStatus] || r.controlStatus || "",
      tr(r.specificControlMeasure, 42),
      tr(r.controlResponsible, 18),
      tr(r.evaluator, 18),
      r.evaluationDate   ? new Date(r.evaluationDate).toLocaleDateString("es-AR")   : "",
      r.nextReassessment ? new Date(r.nextReassessment).toLocaleDateString("es-AR") : "",
    ]);

    // Anchos fijos para que las 21 columnas quepan en A4 horizontal (269 mm útiles)
    const COL_WIDTHS = {
      0:  { cellWidth: 7 },
      1:  { cellWidth: 10 },
      2:  { cellWidth: 12 },
      3:  { cellWidth: 12 },
      4:  { cellWidth: 12 },
      5:  { cellWidth: 14 },
      6:  { cellWidth: 13 },
      7:  { cellWidth: 14 },
      8:  { cellWidth: 9,  halign: "center" },
      9:  { cellWidth: 9,  halign: "center" },
      10: { cellWidth: 9,  halign: "center" },
      11: { cellWidth: 13 },
      12: { cellWidth: 9,  halign: "center" },
      13: { cellWidth: 13 },
      14: { cellWidth: 14 },
      15: { cellWidth: 14 },
      16: { cellWidth: 18 },
      17: { cellWidth: 13 },
      18: { cellWidth: 13 },
      19: { cellWidth: 11 },
      20: { cellWidth: 13 },
    };

    autoTable(doc, {
      head: [PDF_HEADERS],
      body: pdfRows,
      startY: tableStartY,
      styles: { fontSize: 6.5, cellPadding: 1.5, overflow: "linebreak", textColor: [31, 41, 55] },
      headStyles: { fillColor: [100, 142, 188], textColor: 255, fontStyle: "bold", fontSize: 6, cellPadding: 2 },
      columnStyles: COL_WIDTHS,
      didParseCell(data) {
        if (data.section === "body" && data.column.index === 11) {
          const lvl = String(data.cell.raw);
          const col = LEVEL_COLORS_PDF[lvl];
          if (col) { data.cell.styles.textColor = col; data.cell.styles.fontStyle = "bold"; }
        }
      },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      tableLineColor: [229, 231, 235],
      tableLineWidth: 0.3,
    });

    doc.save("evaluacion_riesgos.pdf");
  };

  return (
    <div className="ra-page">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="ra-title-bar">
        <ManagementModuleTitle labelClassName="ra-title-bar__title" />
      </div>

      <div className="ra-year-bar" ref={yearMenuRef}>
        <button
          type="button"
          className="ra-year-bar__toggle"
          onClick={() => setShowYearMenu((v) => !v)}
        >
          <span className="ra-year-bar__label">{filterYear}</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20" className={`ra-year-bar__arrow${showYearMenu ? " ra-year-bar__arrow--open" : ""}`}>
            <circle cx="12" cy="12" r="9"/>
            <path d="M12 8v6m0 0-2.5-2.5M12 14l2.5-2.5"/>
          </svg>
        </button>

        {showYearMenu && (
          <div className="ra-year-menu">
            {yearOptions.map((y) => (
              <button
                type="button"
                key={y}
                className={`ra-year-menu__item${y === filterYear ? " ra-year-menu__item--active" : ""}`}
                onClick={() => { setFilterYear(y); setShowYearMenu(false); }}
              >
                {y}
              </button>
            ))}
          </div>
        )}
      </div>

      {!hasCompany ? (
        <div className="ra-no-site">
          <p>{t("select_company_from_header") || "Selecciona una empresa desde el encabezado"}</p>
        </div>
      ) : (
        <>
          {/* Matriz — temporalmente deshabilitada */}
          {false && <div className="ra-heatmap-wrap">
            <div className="ra-heatmap-head">
              <div>
                {filterMeth === "INSST" ? (
                  <>
                    <div className="ra-heatmap-title">Matriz OHSAS · Probabilidad × Consecuencia del daño</div>
                    <div className="ra-heatmap-sub">Método General INSST (1996) · Matriz 3×3 · click para filtrar por nivel</div>
                  </>
                ) : filterMeth === "NTP330" ? (
                  <>
                    <div className="ra-heatmap-title">Matriz NTP 330 · Nivel de Deficiencia × Nivel de Exposición</div>
                    <div className="ra-heatmap-sub">Nivel de Probabilidad (NP = ND × NE) · Matriz 4×4 · click para filtrar por nivel</div>
                  </>
                ) : (
                  <>
                    <div className="ra-heatmap-title">Matriz de riesgo 5×5 · Severidad × Frecuencia de exposición</div>
                    <div className="ra-heatmap-sub">Riesgos identificados por celda · click para filtrar por nivel</div>
                  </>
                )}
              </div>
              <div className="ra-hm-legend">
                {["BAJO", "MODERADO", "ALTO", "CRITICO"].map(l => (
                  <div key={l} className="ra-hm-legend-item">
                    <div className="ra-hm-legend-dot" style={{ background: LEVEL_CONFIG[l].color }} />
                    {LEVEL_CONFIG[l].label}
                  </div>
                ))}
              </div>
            </div>

            {/* 5×5 grid */}
            {filterMeth === "5x5" && (
              <div className="ra-heatmap-grid">
                <div className="ra-hm-corner">Sev ↓ / FoE →</div>
                {[1, 2, 3, 4, 5].map(foe => (
                  <div key={foe} className="ra-hm-col-label">{foe}</div>
                ))}
                {[5, 4, 3, 2, 1].map(sev => (
                  <React.Fragment key={sev}>
                    <div className="ra-hm-row-label">{sev}</div>
                    {[1, 2, 3, 4, 5].map(foe => {
                      const lvl   = MATRIX_5x5[sev][foe];
                      const count = heatmapCounts[`${sev}-${foe}`] || 0;
                      return (
                        <div
                          key={foe}
                          className={`ra-hm-cell ra-hm-${lvl.toLowerCase()}`}
                          title={`Sev=${sev} FoE=${foe} · ${LEVEL_CONFIG[lvl].label} · ${count} riesgo(s)`}
                          onClick={() => toggleLevel(lvl)}
                        >
                          <span className="ra-hm-val">{sev * foe}</span>
                          {count > 0 && <span className="ra-hm-n">{count}</span>}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* NTP330 4×4 grid (ND × NE) */}
            {filterMeth === "NTP330" && (
              <div className="ra-heatmap-grid ra-ntp-grid">
                <div className="ra-hm-corner">ND ↓ / NE →</div>
                {NE_VALUES.map(ne => (
                  <div key={ne} className="ra-hm-col-label">{ne}</div>
                ))}
                {ND_VALUES.map(nd => (
                  <React.Fragment key={nd}>
                    <div className="ra-hm-row-label">{ND_LABELS[nd]}</div>
                    {NE_VALUES.map(ne => {
                      const np    = nd * ne;
                      const lvl   = getNPLevel(nd, ne);
                      const count = ntpCounts[`${nd}-${ne}`] || 0;
                      return (
                        <div
                          key={ne}
                          className={`ra-hm-cell${lvl === "na" ? " ra-hm-na" : ` ra-hm-${lvl.toLowerCase()}`}`}
                          title={`ND=${nd} NE=${ne} · NP=${np}${lvl !== "na" ? ` · ${LEVEL_CONFIG[lvl].label}` : ""} · ${count} riesgo(s)`}
                          onClick={() => lvl !== "na" && toggleLevel(lvl)}
                        >
                          <span className="ra-hm-val">{nd === 0 ? "—" : np}</span>
                          {count > 0 && <span className="ra-hm-n">{count}</span>}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* INSST/OHSAS 3×3 grid */}
            {filterMeth === "INSST" && (
              <div className="ra-heatmap-grid ra-insst-grid">
                <div className="ra-hm-corner">Prob ↓ / Daño →</div>
                {["LD", "D", "ED"].map(dc => (
                  <div key={dc} className="ra-hm-col-label">{DC_LABELS[dc]}</div>
                ))}
                {["A", "M", "B"].map(prob => (
                  <React.Fragment key={prob}>
                    <div className="ra-hm-row-label">{PROB_LABELS[prob]}</div>
                    {["LD", "D", "ED"].map(dc => {
                      const lvl   = INSST_MATRIX[prob][dc];
                      const code  = OHSAS_CODE[prob][dc];
                      const count = insstCounts[`${prob}-${dc}`] || 0;
                      return (
                        <div
                          key={dc}
                          className={`ra-hm-cell ra-hm-${lvl.toLowerCase()}`}
                          title={`${PROB_LABELS[prob]} × ${DC_LABELS[dc]} · ${code} · ${LEVEL_CONFIG[lvl].label} · ${count} riesgo(s)`}
                          onClick={() => toggleLevel(lvl)}
                        >
                          <span className="ra-hm-val">{code}</span>
                          {count > 0 && <span className="ra-hm-n">{count}</span>}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>}

          {/* Acciones */}
          <div className="ra-filter-actions">
            <div
              className="ra-export-wrap"
              tabIndex={0}
              onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setExportOpen(false); }}
            >
              <button className="ra-btn-outline" onClick={() => setExportOpen(o => !o)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {t("export_btn")}
                <svg viewBox="0 0 12 12" fill="currentColor" width="8" height="8" style={{ marginLeft: 2 }}>
                  <path d="M6 8L2 4h8z"/>
                </svg>
              </button>
              {exportOpen && (
                <div className="ra-export-menu">
                  <button onMouseDown={() => { exportCsv();  setExportOpen(false); }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    CSV
                  </button>
                  <button onMouseDown={() => { exportXlsx(); setExportOpen(false); }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    Excel (XLSX)
                  </button>
                  <button onMouseDown={() => { exportPdf();  setExportOpen(false); }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    PDF
                  </button>
                </div>
              )}
            </div>
            <div
              className="ra-transfer-slot"
              ref={(node) => { if (node && node !== transferSlot) setTransferSlot(node); }}
            />
            {!noSiteInCtx && (
              <button className="ra-new-btn" onClick={openNew} title={t("ra_new_risk")}>
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                  <path d="M8 3v10M3 8h10"/>
                </svg>
              </button>
            )}
          </div>

          {/* KPI cards + filtros + tabla, en una sola card */}
          <div className="ra-card">
            <div className="ra-kpi-row">
              <div className="ra-kpi-card">
                <div className="ra-kpi-head">
                  <span className="ra-kpi-icon">
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M6 10 10 6M6 6h4v4"/></svg>
                  </span>
                  <span className="ra-kpi-label">{t("ra_kpi_total")}</span>
                </div>
                <div className="ra-kpi-value">{kpiTotal}</div>
                <div className="ra-kpi-sub">
                  {kpiReduccion !== null ? `−${kpiReduccion}% ${t("ra_kpi_after_controls")}` : t("ra_kpi_total_sub")}
                </div>
              </div>
              <div className="ra-kpi-card">
                <div className="ra-kpi-head">
                  <span className="ra-kpi-icon">
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M6 10 10 6M6 6h4v4"/></svg>
                  </span>
                  <span className="ra-kpi-label">{t("ra_kpi_critical")}</span>
                </div>
                <div className="ra-kpi-value">{kpiCritico}</div>
                <div className="ra-kpi-sub">{t("ra_kpi_critical_sub")}</div>
              </div>
              <div className="ra-kpi-card">
                <div className="ra-kpi-head">
                  <span className="ra-kpi-icon">
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M6 10 10 6M6 6h4v4"/></svg>
                  </span>
                  <span className="ra-kpi-label">{t("ra_kpi_high")}</span>
                </div>
                <div className="ra-kpi-value">{kpiAlto}</div>
                <div className="ra-kpi-sub">{t("ra_kpi_high_sub")}</div>
              </div>
              <div className="ra-kpi-card">
                <div className="ra-kpi-head">
                  <span className="ra-kpi-icon">
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M6 10 10 6M6 6h4v4"/></svg>
                  </span>
                  <span className="ra-kpi-label">{t("ra_kpi_moderate")}</span>
                </div>
                <div className="ra-kpi-value">{kpiModerado}</div>
                <div className="ra-kpi-sub">{t("ra_kpi_moderate_sub")}</div>
              </div>
              <div className="ra-kpi-card">
                <div className="ra-kpi-head">
                  <span className="ra-kpi-icon">
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M6 10 10 6M6 6h4v4"/></svg>
                  </span>
                  <span className="ra-kpi-label">{t("ra_kpi_low")}</span>
                </div>
                <div className="ra-kpi-value">{kpiBajo}</div>
                <div className="ra-kpi-sub">{t("ra_kpi_low_sub")}</div>
              </div>
            </div>

            <div className="ra-filters">
              <div className="ra-filter-block ra-filter-search">
                <div className="ra-search-wrap">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input
                    type="text"
                    className="ra-filter-input"
                    placeholder="Área, puesto, fuente, tipo, daño, medida..."
                    value={filterSearch}
                    onChange={e => setFilterSearch(e.target.value)}
                  />
                </div>
              </div>
              {noSiteInCtx && sites.length > 0 && (
                <div className="ra-filter-block">
                  <FilterDropdown
                    options={sites.map(s => ({ key: s._id, value: s._id, text: s.name }))}
                    value={filterSite}
                    onChange={setFilterSite}
                    placeholder={t("site")}
                  />
                </div>
              )}
              <div className="ra-filter-block">
                <FilterDropdown
                  options={[
                    { key: "5x5", value: "5x5", text: "Matriz 5×5" },
                    { key: "NTP330", value: "NTP330", text: "NTP 330" },
                    { key: "INSST", value: "INSST", text: "Método INSST" },
                  ]}
                  value={filterMeth}
                  onChange={setFilterMeth}
                  placeholder={t("methodology")}
                />
              </div>
              <div className="ra-filter-block">
                <FilterDropdown
                  options={riskTypes.map(rt => ({ key: rt, value: rt, text: rt }))}
                  value={filterRiskType}
                  onChange={setFilterRiskType}
                  placeholder={t("ra_hazard_type")}
                />
              </div>
              <div className="ra-filter-block">
                <FilterDropdown
                  options={areas.map(a => ({ key: a.id, value: a.id, text: a.name }))}
                  value={filterArea}
                  onChange={setFilterArea}
                  placeholder={t("ra_sector_area")}
                />
              </div>
              <div className="ra-filter-block">
                <FilterDropdown
                  options={[
                    { key: "IMPL", value: "IMPL", text: t("ra_implemented") },
                    { key: "PROG", value: "PROG", text: t("ra_in_progress_ctrl") },
                    { key: "PLAN", value: "PLAN", text: t("ra_planned") },
                    { key: "NONE", value: "NONE", text: t("ra_not_implemented") },
                  ]}
                  value={filterStatus}
                  onChange={setFilterStatus}
                  placeholder={t("ra_control_status")}
                />
              </div>
              <div className="ra-filter-block">
                <MultiFilterDropdown
                  options={["CRITICO", "ALTO", "MODERADO", "BAJO"].map(lvl => ({
                    key: lvl,
                    value: lvl,
                    text: levelConfigT[lvl].label,
                    color: levelConfigT[lvl].color,
                  }))}
                  values={filterLevels}
                  onChange={setFilterLevels}
                  placeholder={t("ra_risk_level")}
                  clearLabel={t("clear_filters")}
                />
              </div>
            </div>

            {/* Tabla */}
            {items === null ? (
              <div className="ra-loading"><LoadingSpinner /></div>
            ) : (
              <RiskAssessmentsTable
                items={filtered}
                getLevel={getLevel}
                levelConfig={levelConfigT}
                onReload={onReload}
                onEdit={openEdit}
                onDelete={openDelete}
                role={role}
                transferSlot={transferSlot}
                extYear={filterYear}
              />
            )}
          </div>
        </>
      )}

      {/* Drawer — nueva / editar evaluación */}
      <div
        className={`ra-drawer__backdrop${showForm ? " ra-drawer__backdrop--open" : ""}`}
        onClick={() => setShowForm(false)}
      />
      <div className={`ra-drawer__panel${showForm ? " ra-drawer__panel--open" : ""}`}>
        <div className="ra-drawer__header">
          <div className="ra-drawer__title">
            {selected ? t("ra_edit_risk") : t("ra_new_risk_form")}
          </div>
          <button className="ra-drawer__close-btn" onClick={() => setShowForm(false)} type="button">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div className="ra-drawer__body">
          {showForm && (
            <RiskAssessmentForm
              riskAssessment={selected}
              close={() => setShowForm(false)}
              onReload={onReload}
            />
          )}
        </div>
      </div>

      <ConfirmDialog
        open={showConfirm}
        title={t("delete")}
        message={`${t("are_you_sure_delete") || "¿Está seguro de que desea eliminar la evaluación de riesgo"} ${confirmItem?.customId ? `#${confirmItem.customId}` : confirmItem?._id ? `#${String(confirmItem._id).slice(-6).toUpperCase()}` : ""}?`}
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
        confirmLabel={t("accept")}
        cancelLabel={t("cancel")}
        confirmDanger
      />
    </div>
  );
}
